import { useEffect, useState } from 'react'
import {
  api,
  type AuditStatus,
  type DriftPreview,
  type JobScheduleEntry,
  type JobsStatus,
  type LicenceStatus,
  type OperationsCapabilities,
} from '../api'
import { AddOnFootnote, mentionsEvaluator, withStars } from '../components/AddOn'
import { JobLine } from '../components/JobLine'
import { Retention } from '../components/Retention'
import { RowMenu } from '../components/RowMenu'

/**
 * Running the appliance — TECHNICAL_DESIGN.md §5.4, §9.2, §12.3.
 *
 * The operator jobs, so routine work does not need a shell on the box. Two of
 * the command-line tools are deliberately absent and the screen says which and
 * why, because their absence otherwise reads as an oversight:
 *
 * **`anubandh migrate` stays on the console.** §11.3 makes migration
 * customer-initiated and preceded by a dump the command refuses to take for
 * them — the database may be theirs, and a backup step that silently did nothing
 * would be worse than none. A button in a browser is exactly the affordance that
 * gets pressed without the dump.
 *
 * **`anubandh bootstrap-admin` cannot be a screen at all**: it is the way into an
 * appliance with no accounts, and a screen needs a session, which needs the
 * account it exists to create.
 *
 * The diagnostics bundle lists its contents *before* it is produced, which is
 * §12.3's requirement rather than a courtesy — it is a file that leaves the
 * customer's network, and nobody should have to open it to find out what they
 * sent.
 *
 * **One job at a time.** Stacked, the page was six cards to scroll past — two of
 * them red — before reaching the one somebody came for, and the destructive ones
 * sat in the same column as the routine ones. The page shows the part chosen from
 * Settings' list down the left (`SettingsNav`), and the two switches that exist
 * only on a demonstration appliance are listed only there.
 */
export type SystemSection =
  | 'activity'
  | 'jobs'
  | 'retention'
  | 'drift'
  | 'bundle'
  | 'sender'
  | 'reset'
  | 'addons'
  | 'licence'
  | 'server'
type Section = SystemSection

/** System's parts, as this appliance offers them. */
export function systemSections(can: OperationsCapabilities | null): [SystemSection, string][] {
  return [
    ['activity', 'Activity record'],
    ['jobs', 'Jobs'],
    ['retention', 'Retention'],
    ['drift', 'Document drift'],
    ['bundle', 'Support bundle'],
    ...(can?.sender_authentication_switch ? [['sender', 'Sender authentication'] as [SystemSection, string]] : []),
    ...(can?.factory_reset ? [['reset', 'Reset'] as [SystemSection, string]] : []),
    ['licence', 'Licence'],
    ['addons', 'Add-ons'],
    ['server', 'Done on the server'],
  ]
}

function lowerFirst(words: string): string {
  return words.charAt(0).toLowerCase() + words.slice(1)
}

function interval(minutes: number): string {
  if (minutes % 60) return `${minutes} minutes`
  return minutes === 60 ? 'hour' : `${minutes / 60} hours`
}

/** When a job runs, changed within its kind: a time of day, or an interval that divides the day. */
function ScheduleEditor({
  job,
  busy,
  onSave,
  onCancel,
}: {
  job: JobScheduleEntry
  busy: boolean
  onSave: (change: { daily_at?: string; every_minutes?: number }) => void
  onCancel: () => void
}) {
  const [at, setAt] = useState(job.daily_at ?? '')
  const [every, setEvery] = useState(job.every_minutes ?? 0)
  const daily = job.daily_at !== null
  return (
    <div className="probe" data-testid="schedule-editor">
      <h4>{job.label}</h4>
      <p className="note">Usually {lowerFirst(job.usual)}. Times are UTC.</p>
      <div className="form">
        {daily ? (
          <label>
            Runs daily at
            <input type="time" value={at} onChange={(e) => setAt(e.target.value)} />
          </label>
        ) : (
          <label>
            Runs every
            <select value={every} onChange={(e) => setEvery(Number(e.target.value))}>
              {(job.intervals ?? []).map((m) => (
                <option key={m} value={m}>
                  {interval(m)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="actions">
        <button disabled={busy || (daily && !at)} onClick={() => onSave(daily ? { daily_at: at } : { every_minutes: every })}>
          Save
        </button>
        <button className="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

export function Operations({
  section: wanted = 'activity',
  relaxed: applianceRelaxed = false,
  onApplianceChanged,
  onJobHistory,
}: {
  /** The part showing, chosen from Settings' list down the left. */
  section?: SystemSection
  /** Whether sender authentication is currently off, from the principal read —
   *  the same value that drives the banner, so the card and the banner cannot
   *  disagree. */
  relaxed?: boolean
  /** Re-read the principal, so the banner appears or clears without a reload. */
  onApplianceChanged?: () => void
  /** Opens Scheduled Jobs, where what each run came to is read. */
  onJobHistory?: () => void
} = {}) {
  const [audit, setAudit] = useState<AuditStatus | null>(null)
  const [drift, setDrift] = useState<DriftPreview | null>(null)
  const [bundle, setBundle] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [can, setCan] = useState<OperationsCapabilities | null>(null)
  const [phrase, setPhrase] = useState('')
  const [authPhrase, setAuthPhrase] = useState('')
  const [relaxed, setRelaxed] = useState(applianceRelaxed)
  const [reset, setReset] = useState<{ admin_email: string; invitation_token: string } | null>(
    null,
  )
  const [jobs, setJobs] = useState<JobsStatus | null>(null)
  const [editing, setEditing] = useState<JobScheduleEntry | null>(null)

  async function loadJobs() {
    try {
      setJobs(await api.jobs())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function load() {
    setError(null)
    try {
      const [a, d, c] = await Promise.all([
        api.auditStatus(),
        api.driftPreview(),
        api.operationsCapabilities(),
      ])
      setAudit(a)
      setDrift(d)
      setCan(c)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function act(work: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await work()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function download() {
    if (!bundle) return
    // Built and downloaded in the browser: the appliance's filesystem is
    // read-only, and a bundle written to a path there is one more copy nobody
    // deletes.
    const url = URL.createObjectURL(new Blob([bundle], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `anubandh-diagnostics-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const parts = systemSections(can)
  // A part that is no longer offered — the switch was removed from the appliance
  // while the page was open — falls back to the first rather than to nothing.
  const section: Section = parts.some(([id]) => id === wanted) ? wanted : 'activity'
  const heading = parts.find(([id]) => id === section)?.[1] ?? 'System'

  // Jobs keeps itself current while it is open, so a job run now shows what it came to
  // without a reload.
  useEffect(() => {
    if (section !== 'jobs') return
    void loadJobs()
    const every = window.setInterval(() => void loadJobs(), 10_000)
    return () => window.clearInterval(every)
  }, [section])

  return (
    <section>
      <header className="page-head">
        <h2>{heading}</h2>
        <p className="note">
          System: housekeeping for this installation. Nothing here changes any limit.
        </p>
      </header>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <div id="system-panel">
        {section === 'activity' && (
          <div className="card">
            {audit === null ? (
              <p className="loading">Checking…</p>
            ) : (
              <>
                <p className={audit.intact ? 'ok' : 'error'}>
                  {audit.intact
                    ? 'The record is intact — nothing has been altered since it was locked.'
                    : `${audit.broken_days.length} day(s) no longer match what was locked: ${audit.broken_days.join(', ')}`}
                </p>
                <p className="note">
                  Locked up to {audit.last_sealed_day ?? 'nothing yet'};{' '}
                  {audit.events_awaiting_seal} earlier entr(ies) not yet locked. This
                  runs overnight on its own — do it here when you need it done now,
                  such as before producing a pack for an auditor.
                </p>
                <div className="actions">
                  <button
                    disabled={busy}
                    onClick={() =>
                      void act(async () => {
                        const done = await api.sealAudit()
                        setNotice(
                          done.sealed
                            ? `Sealed ${done.day} — ${done.events} event(s).`
                            : 'Nothing to seal for yesterday.',
                        )
                        await load()
                      })
                    }
                  >
                    Seal yesterday
                  </button>
                  <button className="ghost" disabled={busy} onClick={() => void load()}>
                    Re-check
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {section === 'jobs' && (
          <div className="card" data-testid="jobs">
            {jobs === null ? (
              <p className="loading">Loading…</p>
            ) : (
              <>
                {/* The worker's own name (host and process) is for support, so it is on
                    hover rather than in the sentence. */}
                <p
                  className={jobs.scheduler.held ? 'ok' : 'error'}
                  data-testid="scheduler"
                  title={jobs.scheduler.held && jobs.scheduler.holder ? `Worker ${jobs.scheduler.holder}` : undefined}
                >
                  {jobs.scheduler.held
                    ? 'Scheduled jobs are running.'
                    : 'Scheduled jobs are not running. Check that the worker container is up.'}
                </p>
                {editing && (
                  <ScheduleEditor
                    key={editing.kind}
                    job={editing}
                    busy={busy}
                    onCancel={() => setEditing(null)}
                    onSave={(change) =>
                      void act(async () => {
                        await api.setJobSchedule(editing.kind, change)
                        setNotice(`${editing.label}: schedule saved.`)
                        setEditing(null)
                        await loadJobs()
                      })
                    }
                  />
                )}
                <div className="table-scroll">
                  <table className="grid stacked" data-testid="schedule">
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>When</th>
                        <th>Last run</th>
                        <th>Next</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.schedule.map((job) => (
                        <tr key={job.kind}>
                          <td>
                            <strong>{job.label}</strong>
                            {!job.enabled && (
                              <span className="clause">
                                Disabled{job.changed_by ? ` by ${job.changed_by}` : ''}
                              </span>
                            )}
                            {job.why_not && <span className="clause">{job.why_not}</span>}
                          </td>
                          <td>
                            {job.cadence}
                            {job.cadence !== job.usual && (
                              <span className="clause">Usually {lowerFirst(job.usual)}</span>
                            )}
                          </td>
                          <td>{job.last ? <JobLine run={job.last} /> : 'Not yet'}</td>
                          <td>{job.next_due ? new Date(job.next_due).toLocaleString() : '—'}</td>
                          <td className="run-actions">
                            <RowMenu
                              label={`Actions for ${job.label}`}
                              disabled={busy}
                              items={[
                                {
                                  label: job.enabled ? 'Disable' : 'Enable',
                                  onSelect: () =>
                                    void act(async () => {
                                      await api.setJobSchedule(job.kind, { enabled: !job.enabled })
                                      setNotice(
                                        job.enabled
                                          ? `${job.label}: disabled. It is not scheduled until it is enabled again.`
                                          : `${job.label}: enabled.`,
                                      )
                                      await loadJobs()
                                    }),
                                },
                                { label: 'Edit', onSelect: () => setEditing(job) },
                                {
                                  label: 'Run now',
                                  disabled: !job.offered,
                                  why: job.why_not ?? undefined,
                                  onSelect: () =>
                                    void act(async () => {
                                      await api.runJob(job.kind)
                                      setNotice(`${job.label}: queued. The worker takes it within a minute.`)
                                      await loadJobs()
                                    }),
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {onJobHistory && (
                  <p className="note">
                    What each run came to, and what it produced, is in{' '}
                    <button type="button" className="ghost" onClick={onJobHistory}>
                      Scheduled Jobs
                    </button>
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {section === 'drift' && (
          <div className="card">
            {drift === null ? (
              <p className="loading">Checking…</p>
            ) : (
              <>
                <p>
                  {drift.documents_with_a_baseline} cited document(s) carry a baseline
                  and would be re-checked.
                </p>
                <p className="note">{drift.detail}</p>
              </>
            )}
          </div>
        )}

        {section === 'bundle' && (
          <div className="card">
            <p className="note">It contains:</p>
            <ul className="hits">
              <li>build and schema version, and the Python and OS release</li>
              <li>the database URL with its password masked</li>
              <li>row counts per table</li>
              <li>
                the last 100 log entries — who did what, and when —{' '}
                <strong>without</strong> the values that changed, so none of your
                limits or clause references are included
              </li>
            </ul>
            <p className="note">
              It contains no limits, no document text and no passwords. It is
              produced only when you ask for it.
            </p>
            <div className="actions">
              <button
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    const result = await api.diagnostics()
                    setBundle(JSON.stringify(result.bundle, null, 2))
                    setNotice('Bundle ready.')
                  })
                }
              >
                Produce it
              </button>
              {bundle && (
                <button className="ghost" onClick={download}>
                  Download
                </button>
              )}
            </div>
          </div>
        )}

        {/* Same shape as the reset below and for the same reason: offered only
            where the appliance permits it, and thrown against a typed phrase
            rather than a click. §8.6's gate is the only thing between an inbound
            email and a proposal to change a client's mandate. */}
        {section === 'sender' && can?.sender_authentication_switch && (
          <div className="card danger-card" data-testid="sender-authentication">
            <p className="note">
              Inbound mail is normally accepted only when your mail server states
              that it verified who sent it. On a demonstration appliance whose host
              writes no such verdict, nothing can ever be accepted — so it can be
              switched off here.
            </p>
            <p className="warning">
              With it off, anyone who can send mail to the guideline mailbox can
              submit as any authorised sender. Every message admitted this way is
              recorded as unauthenticated, and a banner sits on every screen until
              it is switched back on. Never do this on a live installation.
            </p>

            {relaxed ? (
              <>
                <p className="warn" data-testid="authentication-off">
                  Sender authentication is <strong>off</strong> on this appliance.
                </p>
                <button
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await api.setSenderAuthentication(false)
                      setRelaxed(false)
                      onApplianceChanged?.()
                    })
                  }
                >
                  Turn it back on
                </button>
              </>
            ) : (
              <>
                <label>
                  Type <code>{can.sender_authentication_phrase}</code> to confirm
                  <input
                    value={authPhrase}
                    onChange={(e) => setAuthPhrase(e.target.value)}
                  />
                  <small>
                    Typing it is the confirmation — a dialog is too easy to click
                    through.
                  </small>
                </label>
                <button
                  className="danger"
                  disabled={
                    busy ||
                    authPhrase.trim().toLowerCase() !== can.sender_authentication_phrase
                  }
                  onClick={() =>
                    void act(async () => {
                      await api.setSenderAuthentication(true, authPhrase)
                      setRelaxed(true)
                      setAuthPhrase('')
                      onApplianceChanged?.()
                    })
                  }
                >
                  Turn sender authentication off
                </button>
              </>
            )}
          </div>
        )}

        {section === 'reset' && can?.factory_reset && (
          <div className="card danger-card">
            <h3>Restore this appliance to a fresh deployment</h3>
            <p className="note">
              This is a demonstration installation, so it can be put back to how
              it was on the day it was installed. Funds, groups, limits, documents,
              anything sent in by mail, defined terms, sign-offs, the activity log
              and every account are removed. You get one invitation back, to set
              yourself up again.
            </p>
            <p className="warning">
              Nothing can be recovered afterwards. This button exists only on
              demonstration installations and is never present on a live one.
            </p>

            {reset ? (
              <div className="callout">
                <h3>Fresh. Sign in again with this invitation.</h3>
                <p>{reset.admin_email}</p>
                <p className="secret">
                  <code>{reset.invitation_token}</code>
                </p>
                <p className="note">
                  You have been signed out along with everything else. Use this
                  invitation on the sign-in screen to set yourself up again. It is
                  shown once.
                </p>
              </div>
            ) : (
              <>
                <label>
                  Type <code>{can.factory_reset_phrase}</code> to confirm
                  <input value={phrase} onChange={(e) => setPhrase(e.target.value)} />
                  <small>
                    Typing it is the confirmation — a dialog is too easy to click
                    through.
                  </small>
                </label>
                <button
                  className="danger"
                  disabled={busy || phrase.trim().toLowerCase() !== can.factory_reset_phrase}
                  onClick={() =>
                    void act(async () => {
                      setReset(await api.factoryReset(phrase))
                    })
                  }
                >
                  Restore to a fresh deployment
                </button>
              </>
            )}
          </div>
        )}

        {/* Reported, not switched (decision 26). A licensed pack is a fact about
            the installation, and a toggle here would be operated by whoever
            happened to be signed in. */}
        {section === 'addons' && (
          <div className="card">
            {can === null ? (
              <p className="loading">Checking…</p>
            ) : (
              can.add_ons.map((pack) => (
                <div className="addon" key={pack.code} data-testid={`add-on-${pack.code}`}>
                  <div>
                    <h3>{withStars(pack.name)}</h3>
                    <p className="note">
                      {pack.summary}
                      {mentionsEvaluator(pack.name) && <AddOnFootnote />}
                    </p>
                  </div>
                  <span className={`addon-status ${pack.enabled ? 'on' : 'off'}`}>
                    {pack.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              ))
            )}
            <p className="note">
              Packs are enabled for this installation under its licence. To add one,
              contact DORON Tech Solutions.
            </p>
          </div>
        )}

        {section === 'licence' && <LicenceCard />}

        {section === 'retention' && <Retention />}

        {section === 'server' && (
          <div className="card">
            <dl className="roles">
              <div>
                <dt>Schema migration</dt>
                <dd>
                  <code>anubandh migrate</code>, run on the server after a database
                  backup taken with your own tooling — your database may be one your
                  own team manages.
                </dd>
              </div>
              <div>
                <dt>The first administrator</dt>
                <dd>
                  <code>anubandh bootstrap-admin</code>, run on the server. It creates
                  the first account on a brand-new installation, before anyone
                  signs in.
                </dd>
              </div>
              <div>
                <dt>Removing records past their retention</dt>
                <dd>
                  <code>anubandh apply-retention</code> shows what the retention policy would
                  remove, and with <code>--yes</code> removes it. Schedule it nightly after{' '}
                  <code>anubandh seal-audit</code>, and back up first: a removal cannot be undone.
                </dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </section>
  )
}

/** The licence, read-only (§12.4): a commercial fact about the installation, set in its configuration. */
function LicenceCard() {
  const [lic, setLic] = useState<LicenceStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .licence()
      .then(setLic)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [])

  if (error) return <p className="error">{error}</p>
  if (!lic) return <p className="loading">Checking…</p>

  const limit = (n: number | null) => (n === null ? 'No limit' : String(n))
  return (
    <div className="card" data-testid="licence">
      <h3>{lic.customer ?? 'GUARD'}</h3>
      {lic.state === 'expired' && (
        <p className="warning">
          The licence ran to {lic.expires}. Everything stays readable; changes resume as soon as the renewed licence is
          installed.
        </p>
      )}
      {lic.problems.map((p) => (
        <p key={p} className="warning">
          {p} Contact DORON Tech Solutions for a fresh copy.
        </p>
      ))}
      <table className="grid">
        <tbody>
          <tr>
            <th>Desks</th>
            <td>
              {lic.desks_in_use} of {limit(lic.desks).toLowerCase()}
            </td>
          </tr>
          <tr>
            <th>Funds per desk</th>
            <td>{limit(lic.funds_per_desk)}</td>
          </tr>
          <tr>
            <th>Runs until</th>
            <td>{lic.expires ?? 'No end date'}</td>
          </tr>
        </tbody>
      </table>
      <p className="note">
        GUARD is licensed by the desks you bring onto it. To add desks or add-on packs, contact DORON Tech Solutions.
      </p>
    </div>
  )
}
